import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import {
  HStack,
  VStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import { applyPunchPassModalAtom } from "../../../atoms";
import { BookingPunchPassSummaryInfo } from "./BookingChargeItemList";
import { PunchPassSummary_Booking$key } from "./__generated__/PunchPassSummary_Booking.graphql";

/**
 * Payment states that mean money has been collected against the booking's order.
 *
 * The backend refuses a punch pass change on such a booking -- the `:update_booking`
 * policy runs `POS.ensure_has_no_paid_orders/2`, which rejects any order that
 * `Order.paid?/1` considers paid -- so the Edit affordance must not be offered.
 *
 * FULLY_REFUNDED and CANCELED are deliberately absent: no money is held and the
 * backend still permits the change. AUTO_CHARGE, NO_CHARGES, PROCESSING, UNPAID
 * and UNKNOWN are likewise editable.
 */
const PAID_PAYMENT_STATUSES = new Set<string>([
  "PAID",
  "MARKED_PAID",
  "PARTIALLY_PAID",
  "PARTIALLY_REFUNDED",
]);

type PunchPassSummaryProps = {
  shouldShowPunchPassSummary: boolean;
  punchPassInfo: BookingPunchPassSummaryInfo;
  bookingFragmentRef: PunchPassSummary_Booking$key | null;
};

export const PunchPassSummary = ({
  shouldShowPunchPassSummary,
  punchPassInfo,
  bookingFragmentRef,
}: PunchPassSummaryProps): React.ReactNode => {
  const booking = useFragment(PunchPassSummaryFragment, bookingFragmentRef);

  const bookingId = booking?.id ?? "";
  const bookingOwnerName = booking?.ownerClient?.fullName ?? t`Owner`;
  const isBookingPaid = PAID_PAYMENT_STATUSES.has(booking?.paymentStatus ?? "");
  const [applyPunchPassModal, setApplyPunchPassModal] = useAtom(
    applyPunchPassModalAtom
  );

  const toggleApplyPunchPassModal = () => {
    if (applyPunchPassModal) {
      // Closing modal: set to null
      setApplyPunchPassModal(null);
    } else {
      // Opening modal: set context with IDs
      setApplyPunchPassModal({
        type: "booking",
        bookingId,
        ownerName: bookingOwnerName,
      });
    }
  };
  const punchPassText = (() => {
    if (punchPassInfo.hasPunchPassApplied) {
      return t`1 Applied`;
    } else if (punchPassInfo.validPunchPassCount > 0) {
      return t`${punchPassInfo.validPunchPassCount} Available`;
    } else if (punchPassInfo.invalidPunchPassCount > 0) {
      return t`${punchPassInfo.invalidPunchPassCount} Unavailable`;
    }
    return "";
  })();
  return shouldShowPunchPassSummary ? (
    <VStack
      style={{ marginLeft: 36 }}
      marginTop={2}
      borderTopWidth={1}
      borderTopColor="surface-dividers"
      paddingTop={2}
    >
      <HStack alignItems="center" gap={1} justifyContent="space-between">
        <HStack alignItems="center" gap={1}>
          <Text variant="mainBaseMedium" color="text-primary">
            {t`Punch Passes:`}
          </Text>
          <Text
            variant="mainBaseMedium"
            color={
              punchPassInfo.hasValidPunchPass
                ? "text-success"
                : "text-secondary"
            }
          >
            {punchPassText}
          </Text>
        </HStack>
        {!isBookingPaid && (
          <Button
            testID="edit-punch-pass-button"
            accessibilityLabel={t`Edit punch pass`}
            variant="secondary"
            size="tiny"
            color="text-primary"
            onPress={toggleApplyPunchPassModal}
          >
            {t`Edit`}
          </Button>
        )}
      </HStack>
    </VStack>
  ) : null;
};

const PunchPassSummaryFragment = graphql`
  fragment PunchPassSummary_Booking on Booking {
    id
    ownerClient {
      ... on Member {
        fullName
      }
      ... on Customer {
        fullName
      }
    }
    ... on StandardBooking {
      paymentStatus
    }
    ... on LessonBooking {
      paymentStatus
    }
  }
`;
